import orderBy from "lodash-es/orderBy.js";
import cloneDeep from "lodash-es/cloneDeep.js";
import { PublicFilter, UserFilter } from "../../../models";
import { IFilter, cloneFilter, makeDefaultFilter } from "../../Filter";
import { Kernel } from "../../Kernel";
import { DataStoreTable } from "./DataStoreTable";
import { DataProvider, DataProviderArgs } from "../../DataStore/DataProvider";
import fluent, { FluentIterable } from "fluent-iterable";
import { ChangeSet, createChangeSet } from "../../DataStore/ChangeSet";

export const CUSTOM_FILTER_ID = "00000000-0000-0000-0000-000000000001";

export type IUserFilterJson = {
    id: string;
    name: string;
    filterVersion: number;

    /// Json string
    filter: string;
};

export type IPlaceFilter = {
    id: string | undefined;
    name: string;
    filter: IFilter;
    table: FilterSourceType;
};

export function clonePlaceFilter(filter: IPlaceFilter): IPlaceFilter {
    return cloneDeep(filter);
}

export function isCustomFilter(id: string) {
    return id === CUSTOM_FILTER_ID;
}

export function makeDefaultPlaceFilter(): IPlaceFilter {
    return {
        id: CUSTOM_FILTER_ID,
        name: "",
        filter: makeDefaultFilter(),
        table: "user-filters",
    };
}

export type FilterSourceType = "user-filters" | "public-filters";

export type FilterSource = {
    items: IPlaceFilter[];
};

function emptyArrayToUndefined<T>(array?: T[] | null) {
    return !array || (array && array.length === 0) ? undefined : array;
}

function undefinedToEmptyArray<T>(array?: T[] | null) {
    return !array || (array && array.length === 0) ? [] : array;
}

export class FilterTableBase<
    IUserFilter extends { id: string | undefined },
> extends DataStoreTable<IUserFilter> {
    constructor(
        args: DataProviderArgs,
        modelName: string,
        model: any,
        private readonly _sourceTable: FilterSourceType,
        private readonly _createAWS: (props: any) => any
    ) {
        super(
            args,
            modelName,
            model,
            (obj: any) => this._awsToUserFilter(obj, this._sourceTable),
            (obj: any) => this._userFilterToAws(obj)
        );
    }

    protected override _optimisticUpdateItem(_item: any) {
        //TODO: disabled for now as it causes an exception due to the filter not being converted to JSON first
    }

    private _awsToUserFilter(obj: any, table: FilterSourceType): IPlaceFilter {
        const filter = JSON.parse(obj.filter);
        filter.caveRating = emptyArrayToUndefined(filter.caveRating);
        filter.type = emptyArrayToUndefined(filter.type);
        return {
            id: obj.id,
            name: obj.name,
            filter: filter,
            table,
        };
    }

    private _userFilterToAws(obj: IPlaceFilter): UserFilter {
        const filter = cloneFilter(obj.filter);

        filter.caveRating = undefinedToEmptyArray(filter.caveRating);
        filter.type = undefinedToEmptyArray(filter.type);

        const props = {
            id: obj.id,
            name: obj.name,
            filterVersion: 1,
            filter: JSON.stringify(filter),
        };

        if (!obj.id) {
            delete (props as any).id;
        }
        return this._createAWS(props);
    }
}

export class UserFilterTable extends FilterTableBase<IPlaceFilter> {
    constructor(args: DataProviderArgs) {
        super(
            args,
            "users-filters",
            UserFilter,
            "user-filters",
            (props: any) => new UserFilter(props)
        );
    }
}

export class PublicFilterTable extends FilterTableBase<IPlaceFilter> {
    constructor(args: DataProviderArgs) {
        super(
            args,
            "public-filters",
            PublicFilter,
            "public-filters",
            (props: any) => new PublicFilter(props)
        );
    }
}

export class PlaceFilterTable extends DataProvider {
    private readonly _kernel = Kernel.instance();
    private readonly _sources = new Map<FilterSourceType, FilterSource>([
        [
            "user-filters",
            {
                items: [],
            } as FilterSource,
        ],
        [
            "public-filters",
            {
                items: [],
            } as FilterSource,
        ],
    ]);
    private _allItems: IPlaceFilter[] = [];

    constructor(args: DataProviderArgs) {
        super(args, [
            { source: "user-filters", paths: [] },
            { source: "public-filters", paths: [] },
        ]);

        this._kernel.events.subscribe("current-place-filter-changed", () => this._onChanged());
        this._onChanged();
    }

    override async onChangeSet(source: DataProvider, _changeSet: ChangeSet<any>): Promise<void> {
        if (this._sources) {
            const sourceItems = this._sources.get(source.name as FilterSourceType);
            if (sourceItems) {
                sourceItems.items = [...(await source.all())];
            }
            this._onChanged();
        }
    }

    public override async create(filter: IPlaceFilter): Promise<IPlaceFilter> {
        return await this._kernel.dataStore.getSource(filter.table).create(filter);
    }

    public override async update(filter: IPlaceFilter): Promise<IPlaceFilter | undefined> {
        return await this._kernel.dataStore.getSource(filter.table).update({
            ...filter,
            filterVersion: 1,
        });
    }

    public override async delete(id: string, table: FilterSourceType) {
        if (id) {
            await this._kernel.dataStore.getSource(table).delete(id);
        }
    }

    public override async all(): Promise<FluentIterable<IPlaceFilter>> {
        return fluent(this._allItems);
    }

    public override async read(id: string): Promise<IPlaceFilter | undefined> {
        return this._allItems.find((x) => x.id === id);
    }

    private _onChanged() {
        let all: IPlaceFilter[] = [];
        for (const source of this._sources.values()) {
            all = [...all, ...orderBy([...source.items], "name", "asc")];
        }
        const changes = createChangeSet(this._allItems, all);
        this._allItems = all;
        this._notifyListeners(changes);
        this._kernel.events.raiseEvent("filters-table-changed", all);
    }
}
